/**
 * The destinations an adviser may build an SOS Paper link for.
 *
 * This is a mirror of `travelkit/src/data/countriesData.ts` — every country the
 * SOS page can actually render, and nothing else. A link built for a code the
 * page does not carry opens on a country with no hotlines, which is the one
 * failure this whole feature exists to prevent, so the two lists must stay
 * equal in both directions. `travelkit/scripts/check-travelkit-destinations.mjs`
 * pins that; it runs in travelkit's `npm run lint`, and
 * `__tests__/destinations.test.js` here pins it from the web side.
 *
 * Kept as `.js`, not `.ts`, for the same reason `tokenHelper.js` is: the
 * travelkit gate evaluates this file as plain ESM after stripping imports,
 * with no TypeScript toolchain in reach. It has no imports for that reason.
 *
 * Names are carried per language rather than as a bilingual label — the
 * constructor is already inside react-intl and picks the one that matches the
 * adviser's interface language.
 */

export const DESTINATIONS = [
  {
    code: 'JP',
    flag: '🇯🇵',
    callingCode: '+81',
    region: 'asia',
    popular: true,
    name: { tc: '日本', sc: '日本', en: 'Japan' }
  },
  {
    code: 'TH',
    flag: '🇹🇭',
    callingCode: '+66',
    region: 'asia',
    popular: true,
    name: { tc: '泰國', sc: '泰国', en: 'Thailand' }
  },
  {
    code: 'KR',
    flag: '🇰🇷',
    callingCode: '+82',
    region: 'asia',
    popular: true,
    name: { tc: '韓國', sc: '韩国', en: 'South Korea' }
  },
  {
    code: 'TW',
    flag: '🇹🇼',
    callingCode: '+886',
    region: 'asia',
    popular: true,
    name: { tc: '台灣', sc: '台湾', en: 'Taiwan' }
  },
  {
    code: 'GB',
    flag: '🇬🇧',
    callingCode: '+44',
    region: 'europe',
    popular: true,
    name: { tc: '英國', sc: '英国', en: 'United Kingdom' }
  },
  {
    code: 'US',
    flag: '🇺🇸',
    callingCode: '+1',
    region: 'americas',
    popular: true,
    name: { tc: '美國', sc: '美国', en: 'United States' }
  },
  {
    code: 'FR',
    flag: '🇫🇷',
    callingCode: '+33',
    region: 'europe',
    popular: true,
    name: { tc: '法國', sc: '法国', en: 'France' }
  },
  {
    code: 'AU',
    flag: '🇦🇺',
    callingCode: '+61',
    region: 'oceania',
    popular: true,
    name: { tc: '澳洲', sc: '澳洲', en: 'Australia' }
  },
  {
    code: 'DE',
    flag: '🇩🇪',
    callingCode: '+49',
    region: 'europe',
    popular: true,
    name: { tc: '德國', sc: '德国', en: 'Germany' }
  },
  {
    code: 'IT',
    flag: '🇮🇹',
    callingCode: '+39',
    region: 'europe',
    popular: true,
    name: { tc: '意大利', sc: '意大利', en: 'Italy' }
  },
  {
    code: 'SG',
    flag: '🇸🇬',
    callingCode: '+65',
    region: 'asia',
    popular: true,
    name: { tc: '新加坡', sc: '新加坡', en: 'Singapore' }
  },
  {
    code: 'HK',
    flag: '🇭🇰',
    callingCode: '+852',
    region: 'asia',
    popular: true,
    name: { tc: '香港', sc: '香港', en: 'Hong Kong' }
  },
  {
    code: 'CN',
    flag: '🇨🇳',
    callingCode: '+86',
    region: 'asia',
    popular: true,
    name: { tc: '中國內地', sc: '中国内地', en: 'Mainland China' }
  },
  {
    // A destination of its own on the SOS page, like HK and MO. `SZ` is only this
    // page's code for Shenzhen (not ISO — ISO's SZ is Eswatini, not carried here).
    code: 'SZ',
    flag: '🇨🇳',
    callingCode: '+86',
    region: 'asia',
    popular: true,
    name: { tc: '深圳', sc: '深圳', en: 'Shenzhen' }
  },
  {
    code: 'MY',
    flag: '🇲🇾',
    callingCode: '+60',
    region: 'asia',
    popular: true,
    name: { tc: '馬來西亞', sc: '马来西亚', en: 'Malaysia' }
  },
  {
    code: 'CA',
    flag: '🇨🇦',
    callingCode: '+1',
    region: 'americas',
    popular: true,
    name: { tc: '加拿大', sc: '加拿大', en: 'Canada' }
  },
  {
    code: 'NZ',
    flag: '🇳🇿',
    callingCode: '+64',
    region: 'oceania',
    popular: true,
    name: { tc: '紐西蘭', sc: '新西兰', en: 'New Zealand' }
  },
  {
    code: 'VN',
    flag: '🇻🇳',
    callingCode: '+84',
    region: 'asia',
    popular: true,
    name: { tc: '越南', sc: '越南', en: 'Vietnam' }
  },
  {
    code: 'MV',
    flag: '🇲🇻',
    callingCode: '+960',
    region: 'asia',
    popular: true,
    name: { tc: '馬爾代夫', sc: '马尔代夫', en: 'Maldives' }
  },
  {
    code: 'BD',
    flag: '🇧🇩',
    callingCode: '+880',
    region: 'asia',
    popular: false,
    name: { tc: '孟加拉', sc: '孟加拉', en: 'Bangladesh' }
  },
  {
    code: 'MO',
    flag: '🇲🇴',
    callingCode: '+853',
    region: 'asia',
    popular: true,
    name: { tc: '澳門', sc: '澳门', en: 'Macau' }
  },
  {
    code: 'CH',
    flag: '🇨🇭',
    callingCode: '+41',
    region: 'europe',
    popular: false,
    name: { tc: '瑞士', sc: '瑞士', en: 'Switzerland' }
  },
  {
    code: 'EG',
    flag: '🇪🇬',
    callingCode: '+20',
    region: 'africa',
    popular: false,
    name: { tc: '埃及', sc: '埃及', en: 'Egypt' }
  },
  {
    code: 'IE',
    flag: '🇮🇪',
    callingCode: '+353',
    region: 'europe',
    popular: false,
    name: { tc: '愛爾蘭', sc: '爱尔兰', en: 'Ireland' }
  },
  {
    code: 'TR',
    flag: '🇹🇷',
    callingCode: '+90',
    region: 'europe',
    popular: false,
    name: { tc: '土耳其', sc: '土耳其', en: 'Turkey' }
  }
];

export const DEFAULT_DESTINATION_CODE = 'JP';

/**
 * The same shortlist the SOS page puts above its own search box. An adviser and the
 * traveller they are helping should not be looking at two different shortlists.
 */
export const QUICK_PICK_CODES = ['CN', 'SZ', 'JP', 'KR', 'TW', 'TH'];

/** react-intl locale (`en` / `zh` / `zh-Hant-HK`) → the SOS page's language key. */
export function travelKitLangOfLocale(locale) {
  if (locale === 'en') return 'en';
  if (locale === 'zh') return 'sc';
  return 'tc';
}

export function findDestination(code) {
  return DESTINATIONS.find(d => d.code === code);
}

/**
 * The destination's name in one language, falling back down the chain rather
 * than rendering a blank cell: an unnamed country in a list of flags is
 * indistinguishable from a broken build.
 */
export function destinationName(destination, travelKitLang) {
  if (!destination) return '';
  const name = destination.name || {};
  return name[travelKitLang] || name.tc || name.en || destination.code;
}

/** Free-text match over every language, the ISO code and the calling code. */
export function matchesDestination(destination, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return true;
  const name = destination.name || {};
  return (
    (name.tc || '').toLowerCase().includes(q) ||
    (name.sc || '').toLowerCase().includes(q) ||
    (name.en || '').toLowerCase().includes(q) ||
    (destination.code || '').toLowerCase().includes(q) ||
    (destination.callingCode || '').includes(q)
  );
}
